import type { PaginationMeta } from '@/types/api'
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react'
import { PAGE_SIZE_OPTIONS, type PageSize } from '@/hooks/useCrudList'
import { cn } from '@/lib/cn'

interface Props {
  meta: PaginationMeta | null
  onChange: (page: number) => void
  size?: PageSize
  onSizeChange?: (n: PageSize) => void
}

/**
 * Footer padrão de todas as listas — design plano, sem sombras, rounded discreto.
 *
 *  esquerda: seletor de page-size (25/50/75/100) + contador "X de Y"
 *  direita : grupo de botões {|‹} {‹} [3 / 10] {›} {›|}, design connected/segmented
 */
export function Pagination({ meta, onChange, size, onSizeChange }: Props) {
  const total       = meta?.total ?? 0
  const from        = meta?.from ?? 0
  const to          = meta?.to ?? 0
  const currentPage = meta?.current_page ?? 1
  const lastPage    = Math.max(1, meta?.last_page ?? 1)

  return (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-4 text-sm">
      <div className="flex items-center gap-3">
        {onSizeChange && (
          <label className="flex items-center gap-2 text-xs text-gray-500">
            Mostrar
            <select
              value={size ?? 25}
              onChange={(e) => onSizeChange(Number(e.target.value) as PageSize)}
              className="cursor-pointer h-8 px-2 rounded-md border border-gray-200 bg-white text-xs text-gray-700 focus:outline-none focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500"
            >
              {PAGE_SIZE_OPTIONS.map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
            por página
          </label>
        )}
        <div className="text-xs text-gray-500">
          {total > 0
            ? <>Exibindo <strong className="text-gray-700">{from}</strong>–<strong className="text-gray-700">{to}</strong> de <strong className="text-gray-700">{total}</strong></>
            : <>Nenhum registro</>}
        </div>
      </div>

      {/* Grupo segmentado de botões */}
      <div className="inline-flex items-stretch rounded-md ring-1 ring-gray-200 bg-white overflow-hidden divide-x divide-gray-200">
        <PageBtn ariaLabel="Primeira página" disabled={currentPage <= 1} onClick={() => onChange(1)}>
          <ChevronsLeft size={14} />
        </PageBtn>
        <PageBtn ariaLabel="Página anterior" disabled={currentPage <= 1} onClick={() => onChange(currentPage - 1)}>
          <ChevronLeft size={14} />
        </PageBtn>
        <div className="px-3 flex items-center text-xs text-gray-600 select-none whitespace-nowrap">
          Página <strong className="text-gray-800 mx-1">{currentPage}</strong> de <strong className="text-gray-800 ml-1">{lastPage}</strong>
        </div>
        <PageBtn ariaLabel="Próxima página" disabled={currentPage >= lastPage} onClick={() => onChange(currentPage + 1)}>
          <ChevronRight size={14} />
        </PageBtn>
        <PageBtn ariaLabel="Última página" disabled={currentPage >= lastPage} onClick={() => onChange(lastPage)}>
          <ChevronsRight size={14} />
        </PageBtn>
      </div>
    </div>
  )
}

function PageBtn({
  children, onClick, disabled, ariaLabel,
}: {
  children: React.ReactNode
  onClick: () => void
  disabled?: boolean
  ariaLabel: string
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      className={cn(
        'inline-flex items-center justify-center w-9 h-9 transition',
        'text-gray-600 hover:bg-gray-50 active:bg-gray-100',
        'disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer',
      )}
    >
      {children}
    </button>
  )
}
